import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';

// Determine "open now" from an hours string like "Mon-Fri: 9AM-7PM | Sat: 10AM-5PM | Sun: Closed"
const isCurrentlyOpen = (hoursString) => {
  if (!hoursString) return false;
  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  for (const part of hoursString.split('|').map(p => p.trim())) {
    const [days, hours] = part.split(':').map(p => p.trim());
    if (!hours || hours.toLowerCase() === 'closed') continue;
    let dayMatches = false;
    if (days.toLowerCase().includes('mon') && currentDay === 1) dayMatches = true;
    if (days.toLowerCase().includes('tue') && currentDay === 2) dayMatches = true;
    if (days.toLowerCase().includes('wed') && currentDay === 3) dayMatches = true;
    if (days.toLowerCase().includes('thu') && currentDay === 4) dayMatches = true;
    if (days.toLowerCase().includes('fri') && currentDay === 5) dayMatches = true;
    if (days.toLowerCase().includes('sat') && currentDay === 6) dayMatches = true;
    if (days.toLowerCase().includes('sun') && currentDay === 0) dayMatches = true;
    if (days.toLowerCase().includes('mon-fri') && currentDay >= 1 && currentDay <= 5) dayMatches = true;
    if (days.toLowerCase().includes('mon-sat') && currentDay >= 1 && currentDay <= 6) dayMatches = true;
    if (!dayMatches) continue;
    const m = hours.match(/(\d+)(AM|PM)\s*-\s*(\d+)(AM|PM)/i);
    if (m) {
      let oh = parseInt(m[1]); const op = m[2].toUpperCase();
      let ch = parseInt(m[3]); const cp = m[4].toUpperCase();
      if (op === 'PM' && oh !== 12) oh += 12; if (op === 'AM' && oh === 12) oh = 0;
      if (cp === 'PM' && ch !== 12) ch += 12; if (cp === 'AM' && ch === 12) ch = 0;
      if (currentTime >= oh * 60 && currentTime < ch * 60) return true;
    }
  }
  return false;
};

const Footer = () => {
  const { settings } = useSiteSettings();
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const check = () => setIsOpen(isCurrentlyOpen(settings.hours));
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, [settings.hours]);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Inventory', href: '/inventory' },
    { name: 'About Us', href: '/about' },
    { name: 'Apply Now', href: '/apply-now' },
    { name: 'Referral Program', href: '/referral' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand column */}
          <div>
            <Link to="/" className="flex items-center mb-6">
              <img 
                src={settings.logoUrl || "/logo.jpg"} 
                alt={settings.businessName}
                className="h-16 w-auto object-contain bg-white rounded-lg p-1"
              />
            </Link>
            <p className="text-gray-400 mb-6">
              {(settings.aboutText || '').substring(0, 150)}...
            </p>
            <div className="flex gap-4">
              {settings.facebookUrl && (
                <a href={settings.facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors">
                  <Facebook size={18} />
                </a>
              )}
              {settings.instagramUrl && (
                <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors">
                  <Instagram size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    data-testid={`footer-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Our Services</h3>
            <ul className="space-y-3">
              <li><span className="text-gray-400">Vehicle Sales</span></li>
              <li><span className="text-gray-400">Auto Financing</span></li>
              <li><span className="text-gray-400">Trade-Ins</span></li>
              <li><span className="text-gray-400">Consignment</span></li>
              <li><span className="text-gray-400">Vehicle Delivery</span></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-red-500 mt-1 flex-shrink-0" />
                <a href={`tel:${settings.phone}`} className="text-gray-400 hover:text-red-500 transition-colors">{settings.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-red-500 mt-1 flex-shrink-0" />
                <a href={`mailto:${settings.email}`} className="text-gray-400 hover:text-red-500 transition-colors">{settings.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-red-500 mt-1 flex-shrink-0" />
                <span className="text-gray-400">{settings.address}</span>
              </li>
              {settings.hours && (
                <li className="flex items-start gap-3">
                  <Clock size={18} className="text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-400">
                    {settings.hours}
                    <span className={`ml-2 inline-flex items-center gap-1 text-xs font-semibold ${isOpen ? 'text-green-500' : 'text-red-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {isOpen ? 'Open now' : 'Closed'}
                    </span>
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} {settings.businessName}. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-red-500 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-gray-500 hover:text-red-500 transition-colors">Terms of Service</Link>
            <button type="button" onClick={() => window.dispatchEvent(new Event('bcm:consent-open'))} className="text-gray-500 hover:text-red-500 transition-colors">Cookie settings</button>
            <span className="flex items-center gap-1 text-green-500">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              SSL Secure
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
